<template>
  <!--
    AI Tutor thinking presentation (doc 27, ST-02). Visual source of truth:
    the rendered `design-reference/borna-ai-tutor-streaming-thinking-v1.html`
    (sha256 03529aea...6ed5), with the approved deviations of doc 27 §28.3:
    no progress rail, final wording.

    One truthful sentence per phase (thinkingPhase.js) and, when the wait is
    long, one line of reassurance counted from the student's Send. Nothing
    here describes reasoning, and no phrase rotates.

    Screen readers hear one stable label per phase; the animated line is
    hidden from them, so nothing is announced again and again.
  -->
  <div
    class="ai-tutor-thinking"
    :class="{ 'ai-tutor-thinking--leaving': leaving }"
    role="status"
    :data-phase="phase"
    data-testid="ai-tutor-processing-indicator"
  >
    <span class="ai-tutor-thinking__sr" data-testid="ai-tutor-thinking-label">{{
      $t(`aiTutorSetup.chat.thinking.label.${phase}`)
    }}</span>
    <div class="ai-tutor-thinking__row" aria-hidden="true">
      <span class="ai-tutor-thinking__marker">
        <span class="ai-tutor-thinking__icon ai-tutor-thinking__icon--question">
          <student-home-icon name="chat" :size="16" />
        </span>
        <span class="ai-tutor-thinking__icon ai-tutor-thinking__icon--image">
          <student-home-icon name="image" :size="16" />
        </span>
        <span
          class="ai-tutor-thinking__icon ai-tutor-thinking__icon--preparing"
        >
          <student-home-icon name="edit" :size="16" />
        </span>
        <span
          class="ai-tutor-thinking__icon ai-tutor-thinking__icon--reconnecting"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            focusable="false"
          >
            <path d="M4 10.5c4.5-4 11.5-4 16 0" />
            <path d="M7.5 14.2c2.6-2.2 6.4-2.2 9 0" />
            <circle cx="12" cy="18" r="1.1" fill="currentColor" />
          </svg>
        </span>
      </span>
      <strong class="ai-tutor-thinking__line"
        ><span :key="phase" data-testid="ai-tutor-thinking-line">{{
          $t(`aiTutorSetup.chat.thinking.${phase}`)
        }}</span></strong
      >
    </div>
    <p
      v-if="hintKey"
      :key="hintKey"
      class="ai-tutor-thinking__hint"
      data-testid="ai-tutor-thinking-hint"
    >
      {{ $t(`aiTutorSetup.chat.thinking.hint.${hintKey}`) }}
    </p>
  </div>
</template>

<script>
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import { THINKING_HINTS_MS } from './reveal/revealConfig'
import { revealEnvironment } from './reveal/revealEnvironment'
import { THINKING_PHASE } from './thinkingPhase'

const HINT_KEYS = ['after12', 'after25', 'after45']
const TICK_MS = 500

export default {
  name: 'TutorThinking',
  components: { StudentHomeIcon },
  props: {
    /** @type {import('vue').PropOptions<'question'|'image'|'preparing'|'reconnecting'>} */
    phase: {
      type: String,
      default: THINKING_PHASE.QUESTION,
      validator: value => Object.values(THINKING_PHASE).includes(value),
    },
    // The student's Send, in milliseconds on the presentation clock. The
    // reassurance counts from it, not from the phase (doc 27 §28.2 D5). `0`:
    // unknown -- no reassurance is shown.
    since: {
      type: Number,
      default: 0,
    },
    // The status is giving way to the first word of the answer.
    leaving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { waited: 0 }
  },
  computed: {
    hintKey() {
      let key = ''
      THINKING_HINTS_MS.forEach((after, index) => {
        if (this.waited >= after) key = HINT_KEYS[index]
      })
      return key
    },
  },
  watch: {
    since() {
      this.measure()
    },
  },
  created() {
    this.measure()
    this.timer = revealEnvironment.setInterval(this.measure, TICK_MS)
  },
  beforeDestroy() {
    revealEnvironment.clearInterval(this.timer)
    this.timer = null
  },
  methods: {
    measure() {
      this.waited = this.since
        ? Math.max(0, revealEnvironment.now() - this.since)
        : 0
    },
  },
}
</script>

<style lang="scss" scoped>
// Every value below was measured from the rendered design candidate at
// 1440x900 and 390x844 (`.st-think*`), in the chat's own tokens.
$thinking-warn-soft: #fff7e5;
$thinking-warn-text: #855511;

// As tall as a first line of answer plus the typing dots, so the hand-off
// changes nothing about the bubble but its content.
.ai-tutor-thinking {
  display: grid;
  align-content: start;
  gap: 7px;
  min-height: 48px;
  transition: opacity 0.18s ease, transform 0.18s ease;

  &--leaving {
    opacity: 0;
    transform: translateY(-4px);
  }
}

.ai-tutor-thinking__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

// The sentence starts exactly where the first line of the answer will (42px
// below the bubble's top), so the hand-off does not move the text line.
.ai-tutor-thinking__row {
  display: flex;
  align-items: flex-start;
  gap: 9px;
}

// The chat's own marker tile (progress bar: 8px radius).
.ai-tutor-thinking__marker {
  position: relative;
  flex: none;
  width: 26px;
  height: 26px;
  // measured: the tile sits 2.4px below the sentence's line box
  margin-top: 2.4px;
  border-radius: 8px;
  background: $borna-indigo-50;
  color: $borna-indigo-600;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.ai-tutor-thinking__icon {
  position: absolute;
  top: 5px;
  left: 5px;
  display: block;
  width: 16px;
  height: 16px;
  line-height: 0;
  opacity: 0;
  transform: scale(0.6) rotate(-40deg);
  transition: opacity 0.26s ease,
    transform 0.32s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

[data-phase='question'] .ai-tutor-thinking__icon--question,
[data-phase='image'] .ai-tutor-thinking__icon--image,
[data-phase='preparing'] .ai-tutor-thinking__icon--preparing,
[data-phase='reconnecting'] .ai-tutor-thinking__icon--reconnecting {
  opacity: 1;
  transform: none;
}

[data-phase='reconnecting'] .ai-tutor-thinking__marker {
  background: $thinking-warn-soft;
  color: $thinking-warn-text;
}

[data-phase='reconnecting'] .ai-tutor-thinking__icon--reconnecting {
  animation: ai-tutor-thinking-blink 1.6s ease-in-out infinite;
}

[data-phase='preparing'] .ai-tutor-thinking__icon--preparing {
  animation: ai-tutor-thinking-write 1.5s ease-in-out infinite;
}

.ai-tutor-thinking__line {
  display: block;
  min-width: 0;
  min-height: 22.8px;
  color: $borna-ink;
  font-size: 12px;
  font-weight: $borna-fw-bold;
  line-height: 1.9;

  // One sentence; a light passes over it.
  span {
    display: inline-block;
    background-image: linear-gradient(
      90deg,
      $borna-ink 0%,
      $borna-ink 38%,
      $borna-indigo-600 50%,
      $borna-ink 62%,
      $borna-ink 100%
    );
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: ai-tutor-thinking-shimmer 2.8s linear infinite,
      ai-tutor-thinking-in 0.3s ease both;
  }
}

.ai-tutor-thinking__hint {
  margin: 0;
  color: $borna-muted;
  font-size: 10px;
  line-height: 1.8;
  animation: ai-tutor-thinking-in 0.35s ease both;
}

@keyframes ai-tutor-thinking-shimmer {
  from {
    background-position: -75% 0;
  }
  to {
    background-position: 175% 0;
  }
}

@keyframes ai-tutor-thinking-in {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes ai-tutor-thinking-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes ai-tutor-thinking-write {
  0%,
  100% {
    transform: none;
  }
  50% {
    transform: translate(-1.2px, 0.8px) rotate(-7deg);
  }
}

// Reduced motion: the status stays, the decoration stops.
@media (prefers-reduced-motion: reduce) {
  .ai-tutor-thinking,
  .ai-tutor-thinking__icon,
  .ai-tutor-thinking__marker {
    transition: none;
  }

  .ai-tutor-thinking__icon,
  .ai-tutor-thinking__hint {
    animation: none !important;
  }

  .ai-tutor-thinking__line span {
    animation: none;
    background: none;
    color: $borna-ink;
  }
}
</style>
